import {
    createContext,
    useState,
    useContext,
    useCallback,
} from "react";
import {
    createKeyAPICall,
    deleteKeyAPICall,
    getKeysAPICall,
    updateKeyAPICall,
    getUsageBreakdownAPICall,
    getEmbedKeysAPICall,
    createEmbedKeyAPICall,
    updateEmbedKeyAPICall,
    deleteEmbedKeyAPICall,
   
} from "../api/keys/keyService";


export const KeysContext = createContext({
    keys: [],
    keysCount: 0,
    realtimeKeysCount: 0,
    videoApiKeysCount: 0,
    meetingsApiKeysCount: 0,
    embedKeys: [],
    embedKeysCount: 0,
    setKeys: () => { },
    setEmbedKeys: () => { },
    getKeys: () => { },
    getEmbedKeys: () => { },
    createKey: () => { },
    createEmbedKey: () => { },
    deleteKey: () => { },
   
    deleteEmbedKey: () => { },
    updateKey: () => { },
    updateEmbedKey: () => { }
});

const KeysContextProvider = ({ children, setLoading }) => {
    const [keys, setKeys] = useState(undefined);
    const [keysCount, setKeysCount] = useState(0);
    const [realtimeKeysCount, setRealtimeKeysCount] = useState(0);
    const [videoApiKeysCount, setVideoApiKeysCount] = useState(0);
    const [meetingsApiKeysCount, setMeetingsApiKeysCount] = useState(0);
    const [embedKeys, setEmbedKeys] = useState(undefined);
    const [embedKeysCount, setEmbedKeysCount] = useState(0);

    const getKeys = useCallback(async (projectId, page, itemsPerPage, onSuccess, onError, dateFilters = null, productSlug = "api", status = "active") => {
        setKeys(undefined);
        try {
            const [keysResponse, breakdownResponse] = await Promise.all([
                new Promise((resolve, reject) => getKeysAPICall(projectId, page, itemsPerPage, resolve, reject, dateFilters, productSlug, status)),
                new Promise((resolve, reject) => getUsageBreakdownAPICall(projectId, 1, 1000, resolve, reject, dateFilters, productSlug))
            ]);

            const baseKeys = keysResponse.data.keys || [];
            const breakdownItems = Array.isArray(breakdownResponse.data?.items) ? breakdownResponse.data.items : [];

            const isInactive = status === "inactive";
            const usageByKeyId = {};
            breakdownItems.forEach(item => {
                const source = isInactive ? item.deleted_api_key : item.api_key;
                if (!source) return;
                const id = isInactive ? source.api_key_id : source.id;
                if (!usageByKeyId[id]) usageByKeyId[id] = { words: 0, audio: 0, video: 0 };
                usageByKeyId[id].words += item.words || 0;
                usageByKeyId[id].audio += item.audio || 0;
                usageByKeyId[id].video += item.video || 0;
            });

            const mergedKeys = baseKeys.map(baseKey => {
                const usageLookupId = isInactive ? baseKey.api_key_id : baseKey.id;
                const usage = usageByKeyId[usageLookupId] || { words: 0, audio: 0, video: 0 };
                return {
                    id: baseKey.id,
                    name: baseKey.name,
                    key: baseKey.key,
                    details: baseKey.details,
                    created_at: baseKey.created_at,
                    last_used_at: baseKey.last_used_at,
                    words: usage.words,
                    audio_minutes: usage.audio,
                    video_minutes: usage.video,
                    key_type: productSlug,
                    status
                };
            });

            setKeys(mergedKeys);
            if (status === "active") {
                const totalCount = keysResponse.data.pagination?.total_count || 0;
                if (productSlug === "realtime_api") {
                    setRealtimeKeysCount(totalCount);
                } else if (productSlug === "video_translation_api") {
                    setVideoApiKeysCount(totalCount);
                } else if (productSlug === "meetings_api") {
                    setMeetingsApiKeysCount(totalCount);
                } else {
                    setKeysCount(totalCount);
                }
            }
            onSuccess(keysResponse, mergedKeys);

        } catch (error) {
            console.error("Failed to fetch keys or breakdown:", error);
            if (onError) onError(error);
        }
    }, []);

    const createKey = useCallback(async (name, details, projectId, onSuccess, onError, productSlug = "api") => {
        try {
            createKeyAPICall(name, details, projectId, onSuccess, onError, productSlug);
        } catch (error) {
            if (onError) onError(error);
        }
    }, []);

    const updateKey = useCallback(async (projectId, keyId, name, onSuccess, onError, details) => {
        updateKeyAPICall(
            projectId,
            keyId,
            name,
            function (response) {
                onSuccess(response);
            },
            onError,
            details
        );
    }, []); // <-- Added missing dependency array

    const deleteKey = useCallback(async (projectId, keyId, onSuccess, onError) => {
        deleteKeyAPICall(
            projectId,
            keyId,
            function (response) {
                onSuccess(response);
            },
            onError
        );
    }, []); // <-- Added missing dependency array



    // ============= EMBED KEY METHODS =============

    const getEmbedKeys = useCallback(async (projectId, page, itemsPerPage, onSuccess, onError) => {
        getEmbedKeysAPICall(
            projectId,
            page,
            itemsPerPage,
            function (response) {
                // Embed keys are now returned as regular api_keys with website_translation_config
                const keys = (response.data.keys || []).map((key) => ({
                    ...key,
                    allowed_domains: key.website_translation_config?.allowed_domains || [],
                    allowed_languages: key.website_translation_config?.allowed_languages || [],
                }));
                setEmbedKeys(keys);
                setEmbedKeysCount(response.data.pagination?.total_count || 0);
                onSuccess(response);
            },
            onError
        );
    }, []);

    const createEmbedKey = useCallback(async (projectId, name, allowedDomains, allowedLanguages, onSuccess, onError) => {
        createEmbedKeyAPICall(
            projectId,
            name,
            allowedDomains,
            allowedLanguages,
            function (response) {
                onSuccess(response);
            },
            onError
        );
    }, []);

    const updateEmbedKey = useCallback(async (projectId, keyId, name, allowedDomains, allowedLanguages, onSuccess, onError) => {
        updateEmbedKeyAPICall(
            projectId,
            keyId,
            name,
            allowedDomains,
            allowedLanguages,
            function (response) {
                onSuccess(response);
            },
            onError
        );
    }, []);

    const deleteEmbedKey = useCallback(async (projectId, keyId, onSuccess, onError) => {
        deleteEmbedKeyAPICall(
            projectId,
            keyId,
            function (response) {
                onSuccess(response);
            },
            onError
        );
    }, []);

    return (
        <KeysContext.Provider
            value={{
                keys, setKeys, keysCount, realtimeKeysCount, videoApiKeysCount, meetingsApiKeysCount,
                embedKeys, setEmbedKeys, embedKeysCount,
                getKeys, createKey, deleteKey, updateKey,
                
                getEmbedKeys, createEmbedKey, deleteEmbedKey, updateEmbedKey
            }}
        >
            {children}
        </KeysContext.Provider>
    );
};

export const useKeysContext = () => useContext(KeysContext);

export default KeysContextProvider;